import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { Box, Collapse, List, ListItem, ListItemText } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import {
  EnvironmentVariables,
  MoreListProps,
  OrganizationTierTypes,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import { PathConstants } from "@/routes/constants";
import FeatureRouter from "@/modules/common/components/layout/side-menu/old-navbar/feature-router";
import { CirclePlusIcon } from "@/modules/common/components/ui-kit/icons/circle-plus-icon";
import { useLocation } from "react-router-dom";

export const MoreSection = ({
  handleItemClick,
  showUploadLeadsModal,
  StyledListItemButton,
  SubMenuListItemButton,
  getNavLinkStyles,
  erealtyURL,
}: MoreListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const location = useLocation();

  const moreChildPaths = useMemo(
    () => [
      PathConstants.AGENTS_ACTIVITY,
      PathConstants.AGENTS_TARGETS,
      PathConstants.AUTO_DELEGATION_WEIGHTS,
      PathConstants.DASHBOARDS,
      PathConstants.LEAGUE,
      PathConstants.AMBASSADORS,
      PathConstants.LISTING_DASHBOARD,
      PathConstants.LEAD_ICONS,
      PathConstants.SALES_COMMISSIONS,
      PathConstants.DAILY_OPERATIONS,
      PathConstants.COMMENTS_SEARCH,
      PathConstants.AGENTS_TREE,
      PathConstants.RENTAL_INVENTORY,
    ],
    [],
  );

  useEffect(() => {
    const anySelected = moreChildPaths.some((p) => getNavLinkStyles(p));
    if (anySelected) setIsExpanded(true);
  }, [location.pathname, getNavLinkStyles, moreChildPaths]);

  return (
    <>
      <ListItem disablePadding>
        <StyledListItemButton onClick={() => setIsExpanded(!isExpanded)}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: nawyTheme.nawy.spacing.xs,
              }}
            >
              <CirclePlusIcon />
              <ListItemText primary="More" />
            </Box>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
        </StyledListItemButton>
      </ListItem>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <PermissionGuard
            permissions={[
              { name: PermissionsEnum.AGENTS, level: PermissionLevel.READ },
            ]}
          >
            {[
              { label: "Agents Activity", path: PathConstants.AGENTS_ACTIVITY },
              { label: "Agents Targets", path: PathConstants.AGENTS_TARGETS },
              {
                label: "Agents Auto-Delegation",
                path: PathConstants.AUTO_DELEGATION_WEIGHTS,
              },
            ].map((item) => (
              <ListItem disablePadding key={item.label}>
                <SubMenuListItemButton
                  sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                  onClick={() => handleItemClick(item.path)}
                  selected={getNavLinkStyles(item.path)}
                >
                  <ListItemText primary={item.label} />
                </SubMenuListItemButton>
              </ListItem>
            ))}
          </PermissionGuard>

          <PermissionGuard
            permissions={[
              { name: PermissionsEnum.DASHBOARDS, level: PermissionLevel.READ },
            ]}
          >
            <ListItem disablePadding>
              <SubMenuListItemButton
                sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                onClick={() => handleItemClick(PathConstants.DASHBOARDS)}
                selected={getNavLinkStyles(PathConstants.DASHBOARDS)}
              >
                <ListItemText primary="Dashboards" />
              </SubMenuListItemButton>
            </ListItem>
          </PermissionGuard>

          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.SALES_LEAGUE,
                level: PermissionLevel.READ,
              },
            ]}
          >
            <ListItem disablePadding>
              <SubMenuListItemButton
                sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                onClick={() => handleItemClick(PathConstants.LEAGUE)}
                selected={getNavLinkStyles(PathConstants.LEAGUE)}
              >
                <ListItemText primary="Sales League" />
              </SubMenuListItemButton>
            </ListItem>
          </PermissionGuard>

          <ListItem disablePadding>
            <SubMenuListItemButton
              sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
              onClick={() => window.open(erealtyURL, "_blank")}
            >
              <ListItemText primary="E-Realty" />
            </SubMenuListItemButton>
          </ListItem>

          {EnvironmentVariables.COMPANY_NAME.toString() ===
            OrganizationTierTypes.NAWY && (
            <ListItem disablePadding>
              <SubMenuListItemButton
                sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                onClick={() => handleItemClick(PathConstants.AMBASSADORS)}
                selected={getNavLinkStyles(PathConstants.AMBASSADORS)}
              >
                <ListItemText primary="Ambassadors" />
              </SubMenuListItemButton>
            </ListItem>
          )}

          {EnvironmentVariables.COMPANY_NAME.toString() ===
            OrganizationTierTypes.NAWY && (
            <PermissionGuard
              permissions={[
                {
                  name: PermissionsEnum.CAN_VIEW_RATINGS_TAB,
                  level: PermissionLevel.READ,
                },
              ]}
            >
              <ListItem disablePadding>
                <SubMenuListItemButton
                  sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                  onClick={() => handleItemClick(PathConstants.REVIEWS)}
                  selected={getNavLinkStyles(PathConstants.REVIEWS)}
                >
                  <ListItemText primary="Reviews" />
                </SubMenuListItemButton>
              </ListItem>
            </PermissionGuard>
          )}

          <FeatureRouter
            featureKey="lead-listing"
            element={
              <ListItem disablePadding>
                <SubMenuListItemButton
                  sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                  onClick={() =>
                    handleItemClick(PathConstants.LISTING_DASHBOARD)
                  }
                  selected={getNavLinkStyles(PathConstants.LISTING_DASHBOARD)}
                >
                  <ListItemText primary="Listing Dashboard" />
                </SubMenuListItemButton>
              </ListItem>
            }
          />

          <FeatureRouter
            featureKey="lead-icons"
            element={
              <ListItem disablePadding>
                <SubMenuListItemButton
                  sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                  onClick={() => handleItemClick(PathConstants.LEAD_ICONS)}
                  selected={getNavLinkStyles(PathConstants.LEAD_ICONS)}
                >
                  <ListItemText primary="Lead Icons" />
                </SubMenuListItemButton>
              </ListItem>
            }
          />

          <FeatureRouter
            featureKey="sales-agents-commissions"
            element={
              <PermissionGuard
                permissions={[
                  {
                    name: PermissionsEnum.AGENTS_COMMISSIONS,
                    level: PermissionLevel.READ,
                  },
                ]}
              >
                <ListItem disablePadding>
                  <SubMenuListItemButton
                    sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                    onClick={() =>
                      handleItemClick(PathConstants.SALES_COMMISSIONS)
                    }
                    selected={getNavLinkStyles(PathConstants.SALES_COMMISSIONS)}
                  >
                    <ListItemText primary="Agents Commissions" />
                  </SubMenuListItemButton>
                </ListItem>
              </PermissionGuard>
            }
          />

          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.DAILY_OPERATIONS,
                level: PermissionLevel.READ,
              },
            ]}
          >
            <ListItem disablePadding>
              <SubMenuListItemButton
                sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                onClick={() => handleItemClick(PathConstants.DAILY_OPERATIONS)}
                selected={getNavLinkStyles(PathConstants.DAILY_OPERATIONS)}
              >
                <ListItemText primary="Daily Operations" />
              </SubMenuListItemButton>
            </ListItem>
          </PermissionGuard>

          {EnvironmentVariables.COMPANY_NAME.toString() !==
            OrganizationTierTypes.PARTNERS && (
            <PermissionGuard
              permissions={[
                {
                  name: PermissionsEnum.SEARCH_COMMENTS,
                  level: PermissionLevel.READ,
                },
              ]}
            >
              <ListItem disablePadding>
                <SubMenuListItemButton
                  sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                  onClick={() => handleItemClick(PathConstants.COMMENTS_SEARCH)}
                  selected={getNavLinkStyles(PathConstants.COMMENTS_SEARCH)}
                >
                  <ListItemText primary="Search Comments" />
                </SubMenuListItemButton>
              </ListItem>
            </PermissionGuard>
          )}

          <ListItem disablePadding>
            <SubMenuListItemButton
              sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
              onClick={() => handleItemClick(PathConstants.EXPO_DASHBOARD)}
              selected={getNavLinkStyles(PathConstants.EXPO_DASHBOARD)}
            >
              <ListItemText primary="Expo Dashboard" />
            </SubMenuListItemButton>
          </ListItem>
          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.AGENTS_TREE,
                level: PermissionLevel.READ,
              },
            ]}
          >
            <ListItem disablePadding>
              <SubMenuListItemButton
                sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                onClick={() => handleItemClick(PathConstants.AGENTS_TREE)}
                selected={getNavLinkStyles(PathConstants.AGENTS_TREE)}
              >
                <ListItemText primary="Agents Tree" />
              </SubMenuListItemButton>
            </ListItem>
          </PermissionGuard>

          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.RENTAL_INVENTORY,
                level: PermissionLevel.READ,
              },
            ]}
          >
            <ListItem disablePadding>
              <SubMenuListItemButton
                sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                onClick={() => handleItemClick(PathConstants.RENTAL_INVENTORY)}
                selected={getNavLinkStyles(PathConstants.RENTAL_INVENTORY)}
              >
                <ListItemText primary="Rental Inventory" />
              </SubMenuListItemButton>
            </ListItem>
          </PermissionGuard>

          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.UPLOAD_LIST_LEADS,
                level: PermissionLevel.WRITE,
              },
            ]}
          >
            <ListItem disablePadding>
              <SubMenuListItemButton
                sx={{ paddingLeft: nawyTheme.nawy.spacing.xl }}
                onClick={showUploadLeadsModal}
              >
                <ListItemText primary="Upload Leads" />
              </SubMenuListItemButton>
            </ListItem>
          </PermissionGuard>
        </List>
      </Collapse>
    </>
  );
};
